import { useEffect, useMemo, useState } from "react";
import { ChevronLeft, ChevronRight, Code2, Download, Eye, FileCode2, FolderArchive, Maximize2, Minimize2, RotateCw, X } from "lucide-react";
import Markdown, { CodeBlock, CopyButton } from "@/components/vai/Markdown";
import { KIND_LABELS, baseName, fileName, previewable, previewDoc, projectFiles } from "@/components/vai/artifacts";
import { downloadZip } from "@/lib/zip";

// Panneau des artefacts (à droite de la conversation, plein écran sur mobile) :
// aperçu ou code, versions successives, copier, télécharger.

function download(artifact) {
  if (artifact.kind === "project") {
    const base = baseName(artifact);
    downloadZip(base, projectFiles(artifact.content).map((f) => ({ path: `${base}/${f.path}`, content: f.content })));
    return;
  }
  const blob = new Blob([artifact.content], { type: "text/plain;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = fileName(artifact);
  a.click();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

// Projet : liste des fichiers à gauche (au-dessus sur mobile), contenu du fichier choisi.
function ProjectView({ artifact }) {
  const files = projectFiles(artifact.content);
  const [path, setPath] = useState(null);
  const file = files.find((f) => f.path === path) || files[0];
  if (!files.length) return <div className="p-5 text-sm text-white/40">{artifact.complete ? "Aucun fichier dans ce projet." : "Écriture des fichiers…"}</div>;
  const ext = (file.path.split(".").pop() || "").toLowerCase();
  return (
    <div className="flex flex-col sm:flex-row h-full" data-testid="vai-project">
      <ul className="sm:w-56 shrink-0 border-b sm:border-b-0 sm:border-r border-white/[0.07] p-2 space-y-0.5 max-h-40 sm:max-h-none overflow-auto">
        {files.map((f) => (
          <li key={f.path}>
            <button type="button" onClick={() => setPath(f.path)} title={f.path}
              className={`w-full flex items-center gap-1.5 px-2 py-1.5 rounded-md text-left text-xs truncate ${f.path === file.path ? "bg-white/10 text-white" : "text-white/60 hover:bg-white/5 hover:text-white"}`}>
              <FileCode2 className="w-3.5 h-3.5 shrink-0" /><span className="truncate">{f.path}</span>
            </button>
          </li>
        ))}
      </ul>
      <div className="flex-1 min-w-0 overflow-auto p-3"><CodeBlock code={file.content} language={ext} /></div>
    </div>
  );
}

export default function ArtifactPanel({ versions, onClose, wide, onToggleWide }) {
  const [index, setIndex] = useState(versions.length - 1);
  const [tab, setTab] = useState("preview");
  const [reload, setReload] = useState(0);
  // Nouvelle version écrite par le modèle : on l'affiche.
  useEffect(() => { setIndex(versions.length - 1); }, [versions.length]);
  const artifact = versions[Math.min(index, versions.length - 1)];
  const canPreview = previewable(artifact.kind) || artifact.kind === "markdown";
  const shownTab = canPreview ? tab : "code";
  const doc = useMemo(() => (previewable(artifact.kind) && artifact.complete ? previewDoc(artifact) : ""), [artifact]);

  return (
    <aside className="flex flex-col h-full bg-[#0f0f15] border-l border-white/[0.07]" data-testid="vai-artifact-panel">
      <header className="flex items-center gap-2 px-3 h-14 border-b border-white/[0.07] shrink-0">
        <div className="min-w-0 flex-1">
          <p className="text-sm font-medium truncate">{artifact.title}</p>
          <p className="text-[11px] text-white/40">{KIND_LABELS[artifact.kind]}{artifact.language && artifact.kind === "code" ? ` · ${artifact.language}` : ""}{!artifact.complete ? " · écriture…" : ""}</p>
        </div>
        {versions.length > 1 && (
          <div className="flex items-center gap-1 text-xs text-white/50">
            <button type="button" className="p-1 rounded hover:bg-white/10 disabled:opacity-30" disabled={index === 0} onClick={() => setIndex(index - 1)} aria-label="Version précédente"><ChevronLeft className="w-4 h-4" /></button>
            <span>v{index + 1}/{versions.length}</span>
            <button type="button" className="p-1 rounded hover:bg-white/10 disabled:opacity-30" disabled={index === versions.length - 1} onClick={() => setIndex(index + 1)} aria-label="Version suivante"><ChevronRight className="w-4 h-4" /></button>
          </div>
        )}
        {canPreview && (
          <div className="flex rounded-lg bg-white/5 p-0.5 text-xs">
            <button type="button" onClick={() => setTab("preview")} className={`flex items-center gap-1 px-2.5 py-1 rounded-md ${shownTab === "preview" ? "bg-white/10 text-white" : "text-white/50 hover:text-white"}`} data-testid="vai-artifact-preview-tab"><Eye className="w-3.5 h-3.5" />Aperçu</button>
            <button type="button" onClick={() => setTab("code")} className={`flex items-center gap-1 px-2.5 py-1 rounded-md ${shownTab === "code" ? "bg-white/10 text-white" : "text-white/50 hover:text-white"}`} data-testid="vai-artifact-code-tab"><Code2 className="w-3.5 h-3.5" />Code</button>
          </div>
        )}
        <button type="button" onClick={onToggleWide} className="hidden lg:block p-1.5 rounded-lg text-white/50 hover:text-white hover:bg-white/10" aria-label={wide ? "Réduire" : "Agrandir"}>{wide ? <Minimize2 className="w-4 h-4" /> : <Maximize2 className="w-4 h-4" />}</button>
        <button type="button" onClick={onClose} className="p-1.5 rounded-lg text-white/50 hover:text-white hover:bg-white/10" aria-label="Fermer" data-testid="vai-artifact-close"><X className="w-4 h-4" /></button>
      </header>

      <div className="flex-1 min-h-0 overflow-auto">
        {shownTab === "preview" && previewable(artifact.kind) && (
          artifact.complete
            ? <iframe key={`${artifact.id}-${index}-${reload}`} title={artifact.title} srcDoc={doc} sandbox="allow-scripts allow-modals allow-forms allow-popups" className="w-full h-full bg-white border-0" data-testid="vai-artifact-frame" />
            : <div className="h-full flex items-center justify-center text-sm text-white/40">L'aperçu s'affiche quand l'artefact est terminé…</div>
        )}
        {shownTab === "preview" && artifact.kind === "markdown" && <div className="p-5"><Markdown text={artifact.content} /></div>}
        {shownTab === "code" && (artifact.kind === "project"
          ? <ProjectView key={`${artifact.id}-${index}`} artifact={artifact} />
          : <div className="p-3"><CodeBlock code={artifact.content} language={artifact.language || artifact.kind} /></div>)}
      </div>

      <footer className="flex items-center gap-2 px-3 h-12 border-t border-white/[0.07] text-xs text-white/60 shrink-0">
        <CopyButton text={artifact.content} className="px-2.5 py-1.5 rounded-lg hover:bg-white/10 hover:text-white" />
        <button type="button" onClick={() => download(artifact)} disabled={!artifact.complete && artifact.kind === "project"} className="inline-flex items-center gap-1 px-2.5 py-1.5 rounded-lg hover:bg-white/10 hover:text-white disabled:opacity-40" data-testid="vai-artifact-download">
          {artifact.kind === "project" ? <FolderArchive className="w-3.5 h-3.5" /> : <Download className="w-3.5 h-3.5" />}{artifact.kind === "project" ? "Télécharger le .zip" : "Télécharger"}
        </button>
        {shownTab === "preview" && previewable(artifact.kind) && (
          <button type="button" onClick={() => setReload(reload + 1)} className="inline-flex items-center gap-1 px-2.5 py-1.5 rounded-lg hover:bg-white/10 hover:text-white ml-auto"><RotateCw className="w-3.5 h-3.5" />Relancer</button>
        )}
      </footer>
    </aside>
  );
}
